import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { CATEGORIES, AUTH_SIGNUP_URL } from "@/lib/data";

const ease = [0.22, 1, 0.36, 1];

const SAMPLE_ENTRIES = [
  { title: "The Whitmore Estate", region: "Yorkshire, England", years: "1745 — Present" },
  { title: "House of Alderman", region: "Amsterdam, Netherlands", years: "1650 — Present" },
  { title: "The Kowalski Family", region: "Kraków, Poland", years: "1812 — Present" },
  { title: "Sørensen Homestead", region: "Bergen, Norway", years: "c. 1790 — Present" },
  { title: "The Beaumont Line", region: "Provence, France", years: "1701 — Present" },
  { title: "Castellano Estate", region: "Sicily, Italy", years: "1683 — Present" },
];

export default function Category() {
  const { slug } = useParams();
  const cat = CATEGORIES.find((c) => c.slug === slug);

  if (!cat) {
    return (
      <main className="max-w-[1600px] mx-auto px-6 md:px-10 pt-40 pb-32">
        <p className="overline">Not found</p>
        <h1 className="font-serif-display mt-2" style={{ fontSize: "clamp(48px, 8vw, 120px)" }}>
          This wing is closed.
        </h1>
        <Link to="/" className="btn-ghost mt-8" data-testid="back-home">
          <ArrowLeft size={16} /> Back to the index
        </Link>
      </main>
    );
  }

  return (
    <main data-testid={`category-page-${cat.slug}`}>
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="flex items-center gap-3 text-[13px] mb-10">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]" data-testid="cat-breadcrumb-home">
            Index
          </Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">{cat.kicker}</span>
        </div>
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease }}
              className="font-serif-display leading-[0.9] tracking-[-0.03em]"
              style={{ fontSize: "clamp(56px, 10vw, 180px)" }}
            >
              {cat.name.split(" ").slice(0, -1).join(" ") || cat.name}{" "}
              <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                {cat.name.split(" ").length > 1 ? cat.name.split(" ").slice(-1)[0] : ""}
              </span>
            </motion.h1>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="text-[15px] leading-[1.65] text-[color:var(--ink-soft)]">{cat.blurb}</p>
            <div className="mt-6 numeric-index text-[color:var(--ink-soft)]">{cat.count}</div>
          </div>
        </div>
      </section>

      {/* Split image + intro */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-7">
            <div className="artifact aspect-[3/2] border border-[color:var(--ink-line-strong)] overflow-hidden">
              <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
            </div>
            <div className="mt-4 numeric-index text-[color:var(--ink-soft)]">Plate № {cat.kicker.replace("Chapter ", "")}</div>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-10">
            <div className="overline text-[color:var(--ink-soft)] mb-4">§ On this collection</div>
            <p
              className="font-italic-serif italic leading-[1.35]"
              style={{ fontSize: "clamp(22px, 2.2vw, 32px)" }}
            >
              "Every page in this wing began with a question — a name half-remembered,
              a date not written down — and became a record because someone cared enough to ask."
            </p>
            <div className="mt-8 flex gap-3">
              <a href={AUTH_SIGNUP_URL} className="btn-rust" data-testid="cat-cta-signup">
                Contribute to this wing <span aria-hidden>→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured entries */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="overline text-[color:var(--ink-soft)] mb-6">§ Featured entries</div>
        <div className="rule-strong" />
        {SAMPLE_ENTRIES.map((e, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.05, ease }}
            className="grid grid-cols-12 gap-4 py-8 md:py-10 border-b border-[color:var(--ink-line)] items-baseline"
            data-testid={`cat-entry-${i}`}
          >
            <div className="col-span-1 numeric-index text-[color:var(--ink-soft)]">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="col-span-11 md:col-span-6">
              <h3
                className="font-serif-display leading-[1.05]"
                style={{ fontSize: "clamp(22px, 2.5vw, 40px)" }}
              >
                {e.title}
              </h3>
            </div>
            <div className="col-span-6 md:col-span-3 numeric-index text-[color:var(--ink-soft)]">{e.region}</div>
            <div className="col-span-5 md:col-span-2 numeric-index text-[color:var(--ink-soft)]">{e.years}</div>
          </motion.div>
        ))}
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <p className="text-sm text-[color:var(--ink-soft)] font-italic-serif italic">
            These families live in the archive's ledger — the worldwide memorials are searchable today.
          </p>
          <Link to="/" className="btn-ghost !py-2 !px-4 !text-xs" data-testid="cat-search-cta">
            Search the worldwide memorials →
          </Link>
        </div>
      </section>

      {/* Related wings */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="overline text-[color:var(--ink-soft)] mb-6">§ Related wings</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {CATEGORIES.filter((c) => c.slug !== cat.slug).slice(0, 3).map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-8 hover:bg-[color:var(--bone-2)] group"
              style={{ transition: "background-color 400ms ease" }}
              data-testid={`cat-related-${c.slug}`}
            >
              <div className="numeric-index text-[color:var(--ink-soft)]">{c.kicker}</div>
              <h4 className="font-serif-display mt-2" style={{ fontSize: "clamp(24px, 2.4vw, 34px)" }}>
                {c.name}
              </h4>
              <div className="mt-8 numeric-index text-[color:var(--ink-soft)] group-hover:text-[color:var(--rust)]">
                Enter →
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
